/**
 * Session Goals Panel
 * Collapsible panel showing daily rotating goals with progress
 */

import { useState, useEffect, useCallback } from 'react';
import { SessionGoal, GameEvent } from '@/types';
import { sessionGoalManager } from '@/systems';
import { eventBus } from '@/services/EventBus';

const COLLAPSED_KEY = 'sessionGoals:collapsed';

export function SessionGoalsPanel() {
  const [goals, setGoals] = useState<SessionGoal[]>(() => sessionGoalManager.getGoals());
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  });

  const refreshGoals = useCallback(() => {
    setGoals([...sessionGoalManager.getGoals()]);
  }, []);

  useEffect(() => {
    // Refresh when goals complete or reset
    const unsubCompleted = eventBus.on(GameEvent.SESSION_GOAL_COMPLETED, refreshGoals);
    const unsubReset = eventBus.on(GameEvent.SESSION_GOALS_RESET, refreshGoals);

    // Also refresh on fish-caught/sold events for live progress
    const unsubCaught = eventBus.on(GameEvent.FISH_CAUGHT, refreshGoals);
    const unsubSold = eventBus.on(GameEvent.FISH_SOLD, refreshGoals);
    const unsubSweet = eventBus.on(GameEvent.SWEET_SPOT_HIT, refreshGoals);
    const unsubGold = eventBus.on(GameEvent.CURRENCY_GAINED, refreshGoals);

    return () => {
      unsubCompleted();
      unsubReset();
      unsubCaught();
      unsubSold();
      unsubSweet();
      unsubGold();
    };
  }, [refreshGoals]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  };

  if (goals.length === 0) return null;

  const completedCount = goals.filter((g) => g.completed).length;

  return (
    <div className="w-full max-w-md mx-auto bg-gray-800/80 rounded-lg border border-gray-700 overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={toggleCollapsed}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-200 hover:bg-gray-700/50 transition-colors"
      >
        <span>
          Daily Goals ({completedCount}/{goals.length} complete)
        </span>
        <span className="text-gray-400 text-xs">{collapsed ? '▼' : '▲'}</span>
      </button>

      {/* Goal list — collapsible */}
      {!collapsed && (
        <div className="px-3 pb-2 space-y-1.5">
          {goals.map((goal) => (
            <GoalRow key={goal.id} goal={goal} />
          ))}
        </div>
      )}
    </div>
  );
}

function GoalRow({ goal }: { goal: SessionGoal }) {
  const pct = goal.targetValue > 0 ? Math.min(100, (goal.currentValue / goal.targetValue) * 100) : 0;

  return (
    <div
      className={`flex items-center gap-2 text-xs rounded px-2 py-1.5 ${
        goal.completed ? 'bg-green-900/30 text-green-300' : 'bg-gray-900/50 text-gray-300'
      }`}
    >
      {/* Status icon */}
      <span className="shrink-0 w-4 text-center">
        {goal.completed ? '✓' : '○'}
      </span>

      {/* Description + progress */}
      <div className="flex-1 min-w-0">
        <div className={`truncate ${goal.completed ? 'line-through opacity-70' : ''}`}>
          {goal.description}
        </div>
        {!goal.completed && (
          <div className="mt-0.5 h-1 rounded-full bg-gray-700 overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>

      {/* Progress text */}
      <span className="shrink-0 tabular-nums text-[10px] text-gray-400">
        {goal.currentValue}/{goal.targetValue}
      </span>

      {/* Reward */}
      <span className="shrink-0 text-yellow-400 text-[10px] font-medium">
        +{goal.rewardGold}g
      </span>
    </div>
  );
}
